import { action, computed, observable, runInAction } from 'mobx';
import message from 'antd/lib/message';
import WebRequestConsoleService from '../services/WebRequestConsoleService';
import type {
    ExecuteWebRequestConsoleRequest,
    WebRequestConsoleMethod,
    WebRequestConsoleOrigin,
    WebRequestConsoleResult
} from '../types';

type FetchLike = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

export default class WebRequestConsoleStore {
    @observable
    method: WebRequestConsoleMethod = 'GET';

    @observable
    url: string = '';

    @observable
    origin: WebRequestConsoleOrigin = 'client';

    @observable
    headers: string = '{}';

    @observable
    body: string = '';

    @observable
    isExecuting: boolean = false;

    @observable
    result: WebRequestConsoleResult | null = null;

    @observable
    validationError: string | null = null;

    constructor(
        private service: WebRequestConsoleService,
        private fetchImpl: FetchLike = (input, init) => window.fetch(input, init)
    ) {}

    @computed
    get parsedHeaders(): Record<string, string> | null {
        try {
            const parsed = JSON.parse(this.headers || '{}');
            if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
                return null;
            }

            return Object.keys(parsed).reduce<Record<string, string>>((acc, key) => {
                const value = parsed[key];
                acc[key] = value === null || value === undefined ? '' : String(value);
                return acc;
            }, {});
        } catch {
            return null;
        }
    }

    @computed
    get canHaveBody() {
        return this.method !== 'GET' && this.method !== 'HEAD';
    }

    @computed
    get canExecute() {
        return !!this.url.trim() && !!this.parsedHeaders && !this.isExecuting;
    }

    @computed
    get formattedResponseHeaders() {
        return this.result ? JSON.stringify(this.result.headers, null, 2) : '';
    }

    @computed
    get formattedResponseBody() {
        if (!this.result) {
            return '';
        }

        return this.tryFormatJson(this.result.body);
    }

    @action.bound
    setMethod(method: WebRequestConsoleMethod) {
        this.method = method;
    }

    @action.bound
    setUrl(url: string) {
        this.url = url;
    }

    @action.bound
    setOrigin(origin: WebRequestConsoleOrigin) {
        this.origin = origin;
    }

    @action.bound
    setHeaders(headers: string) {
        this.headers = headers;
        this.validationError = this.parsedHeaders ? null : 'Headers must be a JSON object.';
    }

    @action.bound
    setBody(body: string) {
        this.body = body;
    }

    @action.bound
    async executeRequest() {
        const request = this.buildRequest();
        if (!request) {
            return;
        }

        try {
            runInAction(() => {
                this.isExecuting = true;
                this.result = null;
                this.validationError = null;
            });

            const result =
                this.origin === 'client'
                    ? await this.executeClientSide(request)
                    : await this.executeServerSide(request);

            runInAction(() => {
                this.result = result;
            });
        } catch (err) {
            console.error(err);
            message.error('Failed to execute request');
        } finally {
            runInAction(() => {
                this.isExecuting = false;
            });
        }
    }

    @action.bound
    clearResults() {
        this.result = null;
        this.validationError = null;
    }

    private buildRequest(): ExecuteWebRequestConsoleRequest | null {
        if (!this.url.trim()) {
            this.validationError = 'URL is required.';
            message.warning('URL is required');
            return null;
        }

        const headers = this.parsedHeaders;
        if (!headers) {
            this.validationError = 'Headers must be a JSON object.';
            message.warning('Headers must be a JSON object');
            return null;
        }

        const request = {
            method: this.method,
            url: this.url.trim(),
            headers,
            body: this.canHaveBody ? this.body : undefined
        };

        request.headers = this.withDefaultContentTypeHeader(request.headers, request.body);
        return request;
    }

    private async executeServerSide(request: ExecuteWebRequestConsoleRequest): Promise<WebRequestConsoleResult> {
        const resp = await this.service.executeRequest({
            ...request,
            headers: request.headers
        });
        if (resp.isOk()) {
            return resp.value;
        }

        return {
            statusCode: resp.error.status,
            statusText: resp.error.text,
            headers: {},
            body: '',
            errorDetails: resp.error.data?.title || resp.error.text || 'Server-side request failed',
            executionTimeMs: 0,
            origin: 'server'
        };
    }

    private async executeClientSide(request: ExecuteWebRequestConsoleRequest): Promise<WebRequestConsoleResult> {
        const start = Date.now();

        try {
            const response = await this.fetchImpl(request.url, {
                method: request.method,
                headers: request.headers,
                body: this.buildClientSideBody(request.headers, request.body)
            });

            const responseHeaders: Record<string, string> = {};
            response.headers.forEach((value, key) => {
                responseHeaders[key] = value;
            });

            return {
                statusCode: response.status,
                statusText: response.statusText,
                headers: responseHeaders,
                body: await response.text(),
                errorDetails: null,
                executionTimeMs: Date.now() - start,
                origin: 'client'
            };
        } catch (err) {
            return {
                statusCode: null,
                statusText: null,
                headers: {},
                body: '',
                errorDetails: err instanceof Error ? err.message : String(err),
                executionTimeMs: Date.now() - start,
                origin: 'client'
            };
        }
    }

    private withDefaultContentTypeHeader(headers: Record<string, string>, body?: string) {
        if (!body || !body.trim() || Object.keys(headers).some(key => key.toLowerCase() === 'content-type')) {
            return headers;
        }

        return {
            ...headers,
            'Content-Type': 'application/json'
        };
    }

    private buildClientSideBody(headers: Record<string, string>, body?: string): BodyInit | undefined {
        if (!this.canHaveBody || body === undefined) {
            return undefined;
        }

        const contentTypeKey = Object.keys(headers).find(key => key.toLowerCase() === 'content-type');
        if (!contentTypeKey) {
            return body;
        }

        return new Blob([body], { type: headers[contentTypeKey] });
    }

    private tryFormatJson(body: string) {
        if (!body.trim()) {
            return body;
        }

        try {
            return JSON.stringify(JSON.parse(body), null, 2);
        } catch {
            return body;
        }
    }
}
